/**
 * Add Sensing Station Form Composer
 *
 * Simple form for creating sensing stations with:
 * - Basic information (name, geo-location)
 * - Location details (address, road intersections, notes)
 * - Network sites coverage (multi-select)
 * - Station pictures
 *
 * Form metadata and field configurations are loaded from form.yml
 */

import * as React from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  AddSensingStationFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddSensingStationFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderSelectControl,
  renderTextareaControl,
  renderButtonControl,
  renderMediaUploaderControl,
  type ControlConfig,
} from '@/forms/formRenderFunctions'
import { useApiQuery } from '@/api/ApiQueryServiceProvider'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { RadioTower } from 'lucide-react'
import { AppLogger } from '@/lib/logger'
import { useToast } from '@/contexts/ToastContext'
import type { GetNetworkCorridorsResponse, GetNetworkSitesResponse } from '@/types/sensNet'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Component Props
// ============================================================================

export type AddSensingStationComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<AddSensingStationFormData>
  showHeader?: boolean
  submitLabel?: string
}

// ============================================================================
// Field validation via Zod schema
// ============================================================================

const validateField = (name: keyof AddSensingStationFormData | string, value: unknown): string | null => {
  // For ZodEffects (from .superRefine()), we need to access the inner schema
  const schema = (AddSensingStationFormSchema as any)._def?.schema || AddSensingStationFormSchema;
  let shape = schema.shape?.[name as string];

  // Resolve nested paths like "geoLocation.latitude" — otherwise the shape lookup fails
  // and lat/long never show their range error (button silently disables with no message).
  if (!shape && typeof name === 'string' && name.includes('.')) {
    const [parent, child] = name.split('.');
    const parentShape = schema.shape?.[parent];
    const innerParent = (parentShape as any)?._def?.schema ?? parentShape;
    shape = innerParent?.shape?.[child];
  }
  if (!shape) return null;

  const parsed = shape.safeParse(value);
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value';
  }
  return null;
}

// ============================================================================
// Composer Component
// ============================================================================

export function AddSensingStationComposer(props: AddSensingStationComposerProps) {
  const { onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props
  const { toast } = useToast()

  // Track selected corridor for filtering network sites
  const [selectedCorridorId, setSelectedCorridorId] = React.useState<string | undefined>(initialValues?.corridorId)

  // Get field configurations from YAML
  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequiredField = (name: string) => {
    // Backend requires only stationName + location (stationAddress is sent as ''
    // when empty). corridorId is UI-only; coveredBySites isn't a backend field.
    return ['name', 'geoLocation.latitude', 'geoLocation.longitude'].includes(name)
  }

  // Fetch corridors for the corridor dropdown
  const { data: corridorsData } = useApiQuery<GetNetworkCorridorsResponse>('getNetworkCorridors', {})
  const corridorOptions = ((corridorsData as any)?.content?.networkCorridors || (corridorsData as any)?.networkCorridors || []).map((c: any) => ({
    value: String(c.networkCorridorNumber),
    label: c.networkCorridorName ?? c.name ?? '',
  }))

  // Fetch all network sites (raw) for corridor-based filtering
  const { data: sitesData, isLoading: sitesLoading } = useApiQuery<GetNetworkSitesResponse>('getNetworkSites', {})

  // Filter sites by selected corridor
  const filteredSiteOptions = React.useMemo(() => {
    const allSites = (sitesData as any)?.content?.networkSites || (sitesData as any)?.networkSites || []
    const filtered = selectedCorridorId
      ? allSites.filter((site: any) => String(site.networkCorridorNumber) === selectedCorridorId)
      : allSites
    return filtered.map((site: any) => ({ value: String(site.networkSiteNumber), label: site.networkSiteName ?? site.name ?? '' }))
  }, [sitesData, selectedCorridorId])


  const upsertSensingStation = useApiMutation('upsertSensingStation', 'POST', {
    // Invalidate the LIST and the single-record query the edit form reads, so reopening
    // the record shows fresh data (not the 5-min-stale cache).
    invalidates: ['getSensingStations', 'getStationByNumber'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to create sensing station:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: AddSensingStationFormSchema,
    defaultValues: {
      stationNumber: initialValues?.stationNumber,
      corridorId: initialValues?.corridorId ?? '',
      name: initialValues?.name ?? '',
      geoLocation: initialValues?.geoLocation ?? { latitude: '' as any, longitude: '' as any },
      addressIfAvailable: initialValues?.addressIfAvailable ?? '',
      stationRoadIntersections: initialValues?.stationRoadIntersections ?? '',
      coveredBySites: initialValues?.coveredBySites ?? [],
      stationNotes: initialValues?.stationNotes ?? '',
      stationPictures: initialValues?.stationPictures ?? [],
    },
    onSubmit: async ({ value }) => {
      const formValue = value as AddSensingStationFormData
      // Coerce and validate with Zod schema (single source of truth)
      const coerced: AddSensingStationFormData = {
        ...formValue,
        stationNumber: initialValues?.stationNumber,
        name: formValue.name?.trim() ?? '',
        geoLocation: {
          latitude: Number((formValue.geoLocation as any)?.latitude),
          longitude: Number((formValue.geoLocation as any)?.longitude),
        },
        addressIfAvailable: formValue.addressIfAvailable?.trim() || undefined,
        stationRoadIntersections: formValue.stationRoadIntersections?.trim() || undefined,
        stationNotes: formValue.stationNotes?.trim() || undefined,
        coveredBySites: Array.isArray(formValue.coveredBySites) ? formValue.coveredBySites : [],
        // Use objectKeys from form field value
        stationPictures: Array.isArray(formValue.stationPictures)
          ? formValue.stationPictures.map(key => ({
              objectKey: key,
              fileName: key,
              contentType: 'image/jpeg',
              fileSize: 0,
              checksum: '',
            }))
          : [],
      } as any

      const parsed = AddSensingStationFormSchema.safeParse(coerced)

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as keyof AddSensingStationFormData, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        // Surface WHY the form didn't submit instead of returning silently.
        const first = parsed.error.issues[0]
        toast({
          title: 'Cannot save sensing station',
          description: first
            ? `${String(first.path[0] ?? 'Field')}: ${first.message}`
            : 'Please fix the highlighted fields.',
          type: 'error',
        })
        log.error('[SensingStation] validation failed:', parsed.error.issues)
        return
      }

      // Transform and submit
      const apiRequest = transformToApiRequest(parsed.data)
      await upsertSensingStation.mutateAsync(apiRequest)
    },
  })

  // Universal render function — options injected by renderFormContent for dynamic fields
  const renderControl = (control: any, injectedOptions?: { value: string; label: string }[], isLoading = false) => {
    if (control.htmlControl === 'textarea') {
      return renderTextareaControlField(control)
    }
    if (control.htmlControl === 'select' && (control.selectMultiple || control.controlType === 'multi-select')) {
      return renderMultiSelectControlField(control, injectedOptions, isLoading)
    }
    if (control.inputType === 'file' || control.htmlControl === 'file') {
      return renderMediaUploaderFieldNew(control)
    }
    if (control.htmlControl === 'select') {
      return renderSelectControlField(control, injectedOptions)
    }
    return renderTextControlField(control)
  }

  const renderTextControlField = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          // Validate on change too so out-of-range values (e.g. latitude > 90) show their
          // message immediately instead of silently disabling the Save button.
          onChange: ({ value }) =>
            validateField(control.name as keyof AddSensingStationFormData, value) || undefined,
          onBlur: ({ value }) =>
            validateField(control.name as keyof AddSensingStationFormData, value) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            type: control.inputType === 'number' ? 'number' : 'text',
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  const renderTextareaControlField = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(control.name as keyof AddSensingStationFormData, value) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }
          return renderTextareaControl(config, bound)
        }}
      </form.AppField>
    )
  }

  const renderSelectControlField = (control: any, options?: { value: string; label: string }[]) => {
    const isRequired = isRequiredField(control.name)
    const resolvedOptions = options ?? (control.selectOptions || [])

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(control.name as keyof AddSensingStationFormData, value) || undefined,
        }}
        listeners={control.name === 'corridorId' ? {
          onChange: ({ value }) => {
            setSelectedCorridorId(value as string || undefined)
            // Clear site selection when corridor changes
            form.setFieldValue('coveredBySites', [])
          },
        } : undefined}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            options: resolvedOptions,
          }
          return renderSelectControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Render multi-select control (using checkboxes) for coveredBySites
  // options come from filteredSiteOptions (corridor-filtered) passed in via renderFormContent
  const renderMultiSelectControlField = (control: any, options: { value: string; label: string }[] = [], isLoading = false) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          // Re-validate on change too, so checking a site clears the "required"
          // error and re-enables Save (onBlur alone runs on the old value).
          onChange: ({ value }) =>
            validateField(control.name as keyof AddSensingStationFormData, value) || undefined,
          onBlur: ({ value }) =>
            validateField(control.name as keyof AddSensingStationFormData, value) || undefined,
        }}
      >
        {(bound) => {
          const currentValue = (bound.state.value as string[]) || []

          const handleCheckboxChange = (siteId: string, checked: boolean) => {
            const current = Array.isArray(currentValue) ? currentValue : []
            const updated = checked
              ? [...current, siteId]
              : current.filter((id) => id !== siteId)
            if (!bound.state.meta.isTouched) {
              bound.handleBlur()
            }
            bound.handleChange(updated)
          }

          return (
            <div className="space-y-2">
              <Label>
                {control.label}
                {isRequired && <span className="ml-1 text-base font-bold text-destructive">*</span>}
              </Label>
              {isLoading ? (
                <p className="text-sm text-muted-foreground">
                  {control.loadingMessage ?? `Loading ${control.label.toLowerCase()}...`}
                </p>
              ) : options.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {control.emptyMessage ?? `No ${control.label.toLowerCase()} available`}
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                  {options.map((option) => (
                    <div key={option.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`${control.name}-${option.value}`}
                        checked={Array.isArray(currentValue) && currentValue.includes(option.value)}
                        onCheckedChange={(checked) => handleCheckboxChange(option.value, checked === true)}
                      />
                      <Label
                        htmlFor={`${control.name}-${option.value}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {option.label}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
              {bound.state.meta?.errors && bound.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">{bound.state.meta.errors[0]}</p>
              )}
            </div>
          )
        }}
      </form.AppField>
    )
  }

  // Render media uploader field using new pattern
  const renderMediaUploaderFieldNew = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField key={control.name} name={control.name}>
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            description: control.description,
            uploader: control.uploader,
          }
          return renderMediaUploaderControl(config, bound, initialValues?.stationNumber?.toString() ?? 'new')
        }}
      </form.AppField>
    )
  }

  // Render form content with groups
  // Corridor filtering logic lives here — injected into renderControl for dynamic fields
  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []

      if (groupControls.length === 0) return null

      return (
        <div key={group.formGroupName} className="space-y-4">
          {(group as any).formGroupLabel && (
            <h3 className="text-sm font-semibold text-foreground">{(group as any).formGroupLabel}</h3>
          )}
          {groupControls.map((control) => {
            if (control.name === 'corridorId') {
              return renderControl(control, corridorOptions)
            }
            if (control.name === 'coveredBySites') {
              const noCorridorMsg = !selectedCorridorId
                ? 'Select a corridor first to filter network sites'
                : undefined
              return (
                <div key={control.name}>
                  {noCorridorMsg && (
                    <p className="text-sm text-muted-foreground mb-1">{noCorridorMsg}</p>
                  )}
                  {renderControl(control, filteredSiteOptions, sitesLoading)}
                </div>
              )
            }
            return renderControl(control)
          })}
        </div>
      )
    })
  }

  const formBody = (
    <>
      {renderFormContent()}

      <div className="pt-4 flex justify-center gap-3">
        {renderButtonControl({
          label: FORM_CONFIG.cancelButton?.label ?? '',
          variant: 'outline',
          onClick: onCancel,
          visibility: !!onCancel,
          type: 'button'
        })}

        {renderButtonControl({
          label: upsertSensingStation.isPending
            ? FORM_CONFIG.submitButton?.loadingLabel ?? 'Loading...'
            : submitLabel ?? FORM_CONFIG.submitButton?.label ?? '',
          variant: 'default',
          type: 'submit'
        }, { SubmitButton: form.SubmitButton })}
      </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <RadioTower className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}

// Re-exports
export {
  AddSensingStationFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddSensingStationFormData
} from './schema'
